import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { NEW_PROJECT_COLOR, Project } from "shared/types/Project"
import { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Combobox } from "shared/ui/input/combobox/Combobox"

import { ProjectOption } from "../project-option/ProjectOption"

export interface ProjectInputProps extends WithClassName {
  name?: string
  items: SelectOption<string, Project>[] | undefined
  loading?: boolean
  readonly?: boolean
  disabled?: boolean
  hideLabel?: boolean
  required?: boolean
  "data-testid"?: string
}

export const ProjectInput = React.memo(
  ({
    className,
    name = "projectID",
    items,
    loading,
    readonly,
    disabled,
    hideLabel,
    required,
    "data-testid": testId
  }: ProjectInputProps) => {
    const { t } = useTranslation()

    const renderValue = useCallback(
      (value: SelectOption<string, Project> | null) => {
        if (!value?.data) {
          return (
            <span className="combobox__placeholder">
              {t("Project.ChooseProject")}
            </span>
          )
        }

        return (
          <ProjectOption
            title={value.data.title}
            color={value.data.ui?.color || NEW_PROJECT_COLOR}
            isTeamProject={!!value.data.team?.id}
          />
        )
      },
      [t]
    )

    const renderOption = useCallback(
      (option: SelectOption<string, Project>) => {
        if (!option.data) {
          return <span>{option.label}</span>
        }

        return (
          <ProjectOption
            title={option.data.title}
            color={option.data.ui?.color || NEW_PROJECT_COLOR}
            isTeamProject={!!option.data.team?.id}
          />
        )
      },
      []
    )

    return (
      <Combobox.Root
        name={name}
        label={t("Project.Project")}
        className={classNames("project-input", className)}
        readonly={readonly}
        disabled={disabled}
        loading={loading}
        required={required}
        data-testid={testId}
        items={items}
      >
        {!hideLabel && <Combobox.Label />}
        {!readonly && (
          <Combobox.Control>
            <Combobox.Trigger>
              <Combobox.Value>{renderValue}</Combobox.Value>
              <Combobox.Icon />
            </Combobox.Trigger>
            <Combobox.Portal>
              <Combobox.Positioner align="start" sideOffset={8}>
                <Combobox.Popup aria-label={t("Project.Project")}>
                  <Combobox.InputContainer>
                    <Combobox.Input />
                    <Icon name={IconName.SEARCH} />
                  </Combobox.InputContainer>
                  <Combobox.Empty />
                  <Combobox.List>
                    {(option: SelectOption<string, Project>) => (
                      <Combobox.Item key={option.value} value={option}>
                        <Combobox.ItemIndicator />
                        <Combobox.ItemContent>
                          {renderOption(option)}
                        </Combobox.ItemContent>
                      </Combobox.Item>
                    )}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Control>
        )}
        <Combobox.Readonly items={items} />
        <Combobox.ErrorMessage />
      </Combobox.Root>
    )
  }
)
